import { FC, useEffect, useRef } from "react";
import { GrainLoader } from "@/components/ui/GrainLoader";
import { signOut } from "@firebase/auth";
import { useAuth } from "@/contexts/AuthContext";
import { auth } from "@/config/firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetShareInstallCache } from "@/hooks/useSlack";
import { markExplicitLogout } from "@/helpers/auth.helpers";
import {
  GLOBAL_AUTH_REDIRECT,
  REDIRECT_QUERY_PARAM,
  redirectToLogin,
  resolveSwitchAccountReturnUrl,
} from "@/helpers/redirection.helpers";

/**
 * How long switch-account waits for iQ's Firebase sign-out before it leaves the
 * page anyway. Leaving at once can cut off the IndexedDB write that forgets the
 * old user, so the next sign-in briefly restores them. Waiting forever is the
 * old hung-loader bug.
 */
export const SWITCH_ACCOUNT_SIGN_OUT_WAIT_MS = 1500;

export const LogoutComponent: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { removeAuthTokenCookie, removeSarasGlobalCookie, removeUserDetails } = useAuth();
  const logoutStarted = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const redirect = searchParams.get(REDIRECT_QUERY_PARAM);

  useEffect(() => {
    if (logoutStarted.current) return;
    logoutStarted.current = true;

    const switchAccount = redirect === GLOBAL_AUTH_REDIRECT;

    removeAuthTokenCookie();
    // Accounts shows its login page only when saras_global is gone AND the
    // user-details cookie is still present (that is how it decides someone is
    // signed in and should be logged out). Wiping user details here makes
    // accounts reissue a token and send the user straight back into iQ.
    removeSarasGlobalCookie();
    if (!switchAccount) {
      // Clear the user-details cookie (name, email, userId, companyId) so PII
      // does not linger on shared browsers after the user signs out.
      removeUserDetails?.();
    }
    resetShareInstallCache();

    const finish = () => {
      if (switchAccount) {
        redirectToLogin(resolveSwitchAccountReturnUrl());
        return;
      }
      markExplicitLogout();
      navigate("/auth");
    };

    if (switchAccount) {
      // Give iQ Firebase a short window to finish signing out, then leave even
      // if it hangs. Accounts signs out its own session once saras_global is
      // missing.
      const signedOut = signOut(auth).catch((err: unknown) => console.error("Sign-out failed", err));
      const timedOut = new Promise<void>((resolve) => {
        timeoutRef.current = setTimeout(resolve, SWITCH_ACCOUNT_SIGN_OUT_WAIT_MS);
      });
      void Promise.race([signedOut, timedOut]).finally(() => {
        clearTimeout(timeoutRef.current);
        finish();
      });
      return;
    }

    signOut(auth)
      .catch((err: unknown) => console.error("Sign-out failed", err))
      .then(finish);
  }, [
    navigate,
    removeAuthTokenCookie,
    removeSarasGlobalCookie,
    removeUserDetails,
    redirect,
  ]);
  return <GrainLoader />;
};
